import React, { useState } from "react";
import { toast } from "sonner";
import { Send, HeartHandshake } from "lucide-react";
import client, { formatApiError } from "../lib/api";
import Layout, { PageHero } from "../components/site/Layout";
import { INTEREST_AREAS } from "../lib/data";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function Support() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ fullName: "", mobileNumber: "", email: "", areaOfInterest: "", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.areaOfInterest) { toast.error("Please select an area of interest."); return; }
    setBusy(true);
    try {
      await client.post("/interest", form);
      toast.success("Your expression of interest has been received. Thank you.");
      setForm({ fullName: "", mobileNumber: "", email: "", areaOfInterest: "", message: "", website: "" });
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <PageHero eyebrow="Support Our Mission" title="We are not currently accepting monetary donations." />
      <section className="mx-auto max-w-4xl px-5 py-16 lg:px-10 lg:py-20">
        <div className="border border-gold/25 bg-white p-8 lg:p-12">
          <HeartHandshake className="text-gold" size={34} strokeWidth={1.3} />
          <div className="mt-6 space-y-4 text-base leading-relaxed text-ink-muted lg:text-lg">
            <p>Sri Akilandeswari Amman Trust is presently developing its initiatives and operational programs. At this stage, we are not accepting monetary donations through the website.</p>
            <p>However, we welcome individuals, organizations and well-wishers who are interested in supporting our future initiatives, contributing their time or expertise, volunteering, or exploring opportunities for collaboration.</p>
            <p>If you would like to express your interest, please register your details below. Our team will review your enquiry and get in touch with you.</p>
          </div>
          {!open && (
            <button onClick={() => setOpen(true)} data-testid="open-interest-form" className="mt-8 inline-flex items-center gap-2 bg-maroon px-7 py-4 text-xs uppercase tracking-wide text-ivory transition-colors duration-300 hover:bg-maroon-dark">
              Register Your Interest
            </button>
          )}
        </div>

        {open && (
          <form onSubmit={submit} className="mt-8 space-y-5 border border-gold/25 bg-white p-8 lg:p-12 animate-fade-up" data-testid="interest-form">
            <h2 className="text-2xl font-medium text-maroon">Register Your Interest</h2>
            <input type="text" name="website" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
            <div className="grid gap-5 sm:grid-cols-2">
              <div><Label htmlFor="i-name">Full Name *</Label><Input id="i-name" required value={form.fullName} onChange={set("fullName")} data-testid="interest-name" className="mt-2" /></div>
              <div><Label htmlFor="i-mobile">Mobile Number *</Label><Input id="i-mobile" required value={form.mobileNumber} onChange={set("mobileNumber")} data-testid="interest-mobile" className="mt-2" /></div>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div><Label htmlFor="i-email">Email Address *</Label><Input id="i-email" type="email" required value={form.email} onChange={set("email")} data-testid="interest-email" className="mt-2" /></div>
              <div>
                <Label>Area of Interest *</Label>
                <Select value={form.areaOfInterest} onValueChange={(v) => setForm({ ...form, areaOfInterest: v })}>
                  <SelectTrigger className="mt-2" data-testid="interest-area"><SelectValue placeholder="Select an area" /></SelectTrigger>
                  <SelectContent>
                    {INTEREST_AREAS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label htmlFor="i-message">Message *</Label><Textarea id="i-message" required rows={5} value={form.message} onChange={set("message")} data-testid="interest-message" className="mt-2" /></div>
            <button type="submit" disabled={busy} data-testid="interest-submit" className="inline-flex items-center gap-2 bg-maroon px-7 py-3.5 text-xs uppercase tracking-wide text-ivory transition-colors duration-300 hover:bg-maroon-dark disabled:opacity-60">
              {busy ? "Submitting..." : <>Submit <Send size={15} /></>}
            </button>
          </form>
        )}
      </section>
    </Layout>
  );
}
